import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { OpenGraphHead } from '../components/OpenGraphHead';
import './NotFoundPage.css';

const REPORT_SAMPLE_RATE = 0.1;
const MISSING_ROUTE_ENDPOINT = '/api/public-routes/missing';

function normalizePathname(pathname: string): string {
  const normalized = `/${pathname}`.replace(/\/+/g, '/').replace(/\/+$/, '');
  return normalized || '/';
}

function getReferrerHost(): string {
  if (!document.referrer) {
    return '';
  }

  try {
    return new URL(document.referrer).host;
  } catch {
    return '';
  }
}

function getUaClass(): 'bot' | 'mobile' | 'tablet' | 'desktop' {
  const userAgent = navigator.userAgent.toLowerCase();

  if (/bot|crawler|spider|slurp|bingpreview/.test(userAgent)) {
    return 'bot';
  }
  if (/ipad|tablet|kindle|silk/.test(userAgent)) {
    return 'tablet';
  }
  if (/mobi|android|iphone|ipod/.test(userAgent)) {
    return 'mobile';
  }
  return 'desktop';
}

function getRequestId(): string | undefined {
  return (
    document.querySelector<HTMLMetaElement>('meta[name="request-id"]')?.content ||
    document.querySelector<HTMLMetaElement>('meta[name="x-request-id"]')?.content ||
    undefined
  );
}

const NotFoundPage: React.FC = () => {
  const { pathname } = useLocation();
  const normalizedPathname = normalizePathname(pathname);

  useEffect(() => {
    if (Math.random() >= REPORT_SAMPLE_RATE) {
      return;
    }

    const requestId = getRequestId();
    const payload = JSON.stringify({
      pathname: normalizedPathname,
      referrerHost: getReferrerHost(),
      uaClass: getUaClass(),
      ...(requestId ? { requestId } : {}),
    });

    if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(MISSING_ROUTE_ENDPOINT, payload)) {
      return;
    }

    void fetch(MISSING_ROUTE_ENDPOINT, {
      method: 'POST',
      body: payload,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      keepalive: true,
    }).catch(() => {
      // Missing-route telemetry must never interfere with the visitor experience.
    });
  }, [normalizedPathname]);

  return (
    <>
      <OpenGraphHead
        title="Page Not Found | Alert1"
        description="The Alert1 page you requested could not be found. Explore our medical alert systems or contact our team for help."
        pathOverride={normalizedPathname}
        noIndex
      />
      <section className="not-found-page" aria-labelledby="not-found-title">
        <div className="not-found-page__content">
          <p className="not-found-page__eyebrow">Alert1 Medical Alert Systems</p>
          <div className="not-found-page__code" aria-hidden="true">404</div>
          <h1 id="not-found-title">We couldn&apos;t find that page</h1>
          <p className="not-found-page__lead">
            The link may be outdated or the address may have been entered incorrectly.
            We can help you get back to the information you need.
          </p>

          <nav className="not-found-page__links" aria-label="Helpful pages">
            <Link className="not-found-page__primary-link" to="/">
              Return to the Alert1 homepage
            </Link>
            <Link to="/medical-alerts">
              Explore medical alert systems
            </Link>
            <Link to="/faq">
              Visit frequently asked questions
            </Link>
            <Link to="/contact">
              Contact Alert1
            </Link>
          </nav>

          <div className="not-found-page__help">
            <h2>Prefer to speak with someone?</h2>
            <p>
              Our friendly team can help you find the right information.
              Call <a href="tel:1-877-579-9862">1-877-579-9862</a>.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default NotFoundPage;
